import { actions, ActorRefFrom, assign, createMachine, InterpreterFrom, StateMachine } from "xstate"
import { CruiseCode } from "@ssc-booking/functional-support"
import { UUID } from "@ssc-booking/functional-support/src/uuid"
import {
    BookingFareSelected,
    buildBookingCompleted,
    buildBookingCourtesyHoldCreated,
    buildBookingDeleted,
    buildBookingFareSelected,
    buildBookingGuestInfoSubmitted,
    buildBookingGuestInfoVisited,
    buildBookingGuestsChanged,
    buildBookingStarted,
    buildBookingSuiteCategorySelected,
    buildBookingSuiteCategoryVisited,
    buildBookingSuiteNumberSelected,
    PostUserEventsRequest,
} from "@silversea-ssc/dotcom-api-types/UserEvents"
import { useActor } from "@xstate/react"
import { AirParams, Occupancy, redirectCheckoutUrl, SuiteNumber } from "@ssc-booking/api-contracts"
import * as Int from "@silversea-ssc/data-types/Int"
import * as DateTime from "@silversea-ssc/data-types/DateTime"
import { assertEvent } from "../../../_common/xstate-helpers"
import { presentParamsFromContext, setQueryParams } from "../query-params"
import { FareCard } from "../read-models/fare-card"
import { fareCardByKey } from "../read-models/fare"
import { Ev } from "../../../_common/machines/event"
import type { ECommerceEvent, ECommerceState } from "./ecommerce-machine/e-commerce-machine"
import {
    CheckoutState,
    FareCardRef,
    SuiteCategorySource,
    VsCodeState,
} from "./ecommerce-machine/e-commerce-machine-context"
import { updateFareCard } from "./ecommerce-machine/e-commerce-machine-helpers"

type InitialUserEventsContext = {
    sessionId: UUID
    restoreCompleted: boolean
    cruiseCode: CruiseCode
    guests: Occupancy
    checkoutStatus: CheckoutState
    vsCode: VsCodeState
    fareCards: readonly FareCard[]
    selectedFare?: FareCardRef
    selectedSuiteCategory?: string
    selectedSuiteCategorySource?: SuiteCategorySource
    selectedSuiteNumber?: SuiteNumber
    eventToSend?: PostUserEventsRequest
}

type EnabledContext = InitialUserEventsContext & {
    userId: string
}

export type UserEventsTypeState =
    | { value: "disabled"; context: InitialUserEventsContext }
    | { value: "enabled"; context: EnabledContext }

export type UserEventsContext = InitialUserEventsContext & Partial<EnabledContext>
export type UserEventsEvents = ECommerceEvent | Ev<"SET_USER_ID", { userId: string }> | Ev<"RESET_USER_ID">

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserEventsStateMachine = StateMachine<UserEventsContext, any, UserEventsEvents, UserEventsTypeState>
type UserEventsInterpreter = InterpreterFrom<UserEventsStateMachine>
export type UserEventsState = UserEventsInterpreter["state"]
export type UserEventsSend = UserEventsInterpreter["send"]
type UserEventsActorRef = ActorRefFrom<UserEventsStateMachine>
type UserEventsTypes = {
    Context: UserEventsContext
    Events: UserEventsEvents
    TypeState: UserEventsTypeState
    StateMachine: UserEventsStateMachine
    Interpreter: UserEventsInterpreter
    State: UserEventsState
    Send: UserEventsSend
    ActorRef: UserEventsActorRef
}

type SendUserEvent = (event: PostUserEventsRequest) => Promise<void>
type WindowLocation = () => URL

const SEND_EVENT = ["sendEvent", "resetEventToSend"]

export const userEventsMachine = (
    context: UserEventsContext,
    sendUserEvent: SendUserEvent,
    windowLocation: WindowLocation = defaultWindowLocation,
): UserEventsStateMachine => {
    type Context = UserEventsTypes["Context"]
    type Events = UserEventsTypes["Events"]
    type TypeState = UserEventsTypes["TypeState"]

    return createMachine<Context, Events, TypeState>(
        {
            id: "userEvents",
            initial: "restoring",
            predictableActionArguments: true,
            context,
            states: {
                restoring: {
                    on: {
                        NO_CHECKOUT_TO_RESTORE: [
                            {
                                cond: "isUserIdSet",
                                target: "enabled",
                                actions: ["setRestoreCompleted", "setBookingStarted"],
                            },
                            { target: "disabled", actions: ["setRestoreCompleted", "setBookingStarted"] },
                        ],
                        SET_USER_ID: [
                            { cond: "isRestoreCompleted", target: "enabled", actions: ["setUserId"] },
                            { actions: ["setUserId"] },
                        ],
                        SESSION_ID_CHANGED: { actions: ["setSessionId"] },
                        CHECKOUT_STARTED: { actions: ["setCheckoutResponse"] },
                        CHECKOUT_COMPLETED: { actions: ["setBookingCompleted"] },
                        CHECKOUT_FAILED: { actions: ["setBookingDeleted"] },
                        CHECKOUT_RESTORED: [
                            { cond: "isUserIdSet", target: "enabled" },
                            { target: "disabled", actions: ["setRestoreCompleted"] },
                        ],
                    },
                },
                disabled: {
                    on: {
                        SET_USER_ID: { target: "enabled", actions: ["setUserId"] },
                        GUESTS_CHANGED: { actions: ["setGuests"] },
                        FARE_CARDS_LOADED: { actions: ["setFareCards"] },
                        FARE_SELECTED: { actions: ["setSelectedFare"] },
                        FARE_CARD_CHANGED: { actions: ["updateFareCards"] },
                        SUITE_CATEGORY_SELECTED: { actions: ["setSelectedSuiteCategory"] },
                        SUITE_NUMBER_SELECTED: { actions: ["setSelectedSuiteNumber"] },
                        CHECKOUT_STARTED: [
                            {
                                cond: "isCourtesyHoldCheckoutResponse",
                                actions: ["setCheckoutResponse", "setCourtesyHoldCreated"],
                            },
                            { actions: ["setCheckoutResponse"] },
                        ],
                        CHECKOUT_COMPLETED: { actions: ["setBookingCompleted"] },
                        ACTIVE_STEP_READY: [
                            { cond: "isSuiteCategoryStep", actions: ["setSuiteCategoryVisited"] },
                            { cond: "isGuestInfoStep", actions: ["setGuestInfoVisited"] },
                            { cond: "isPaymentStep", actions: ["setGuestInfoSubmitted"] },
                        ],
                    },
                },
                enabled: {
                    entry: SEND_EVENT,
                    on: {
                        RESET_USER_ID: { target: "disabled", actions: ["resetUserId"] },
                        SET_USER_ID: { actions: ["setUserId"] },
                        GUESTS_CHANGED: { actions: ["setGuests", ...SEND_EVENT] },
                        FARE_CARDS_LOADED: { actions: ["setFareCards"] },
                        FARE_SELECTED: { actions: ["setSelectedFare", ...SEND_EVENT] },
                        FARE_CARD_CHANGED: { actions: ["updateFareCards", ...SEND_EVENT] },
                        SUITE_CATEGORY_SELECTED: { actions: ["setSelectedSuiteCategory", ...SEND_EVENT] },
                        SUITE_NUMBER_SELECTED: { actions: ["setSelectedSuiteNumber", ...SEND_EVENT] },
                        CHECKOUT_STARTED: [
                            {
                                cond: "isCourtesyHoldCheckoutResponse",
                                actions: ["setCheckoutResponse", "setCourtesyHoldCreated", ...SEND_EVENT],
                            },
                            { actions: ["setCheckoutResponse"] },
                        ],
                        ACTIVE_STEP_READY: [
                            { cond: "isSuiteCategoryStep", actions: ["setSuiteCategoryVisited", ...SEND_EVENT] },
                            { cond: "isGuestInfoStep", actions: ["setGuestInfoVisited", ...SEND_EVENT] },
                            { cond: "isPaymentStep", actions: ["setGuestInfoSubmitted", ...SEND_EVENT] },
                        ],
                        PAYMENT_CONFIRMED: { actions: ["setBookingCompleted", ...SEND_EVENT] },
                        WEB_QUOTE_VALIDATED: { actions: ["setBookingCompleted", ...SEND_EVENT] },
                        SESSION_ID_CHANGED: { actions: ["setSessionId"] },
                    },
                },
            },
        },
        {
            guards: {
                isSuiteCategoryStep: (ctx: Context, ev: Events) => {
                    assertEvent(ev, "ACTIVE_STEP_READY")
                    return ev.activeStep === "category" && !ctx.selectedSuiteCategory
                },
                isGuestInfoStep: (_: Context, ev: Events) => {
                    assertEvent(ev, "ACTIVE_STEP_READY")
                    return ev.activeStep === "guests-info"
                },
                isPaymentStep: (_: Context, ev: Events) => {
                    assertEvent(ev, "ACTIVE_STEP_READY")
                    return ev.activeStep === "payment"
                },
                isCourtesyHoldCheckoutResponse: (_: Context, ev: Events) => {
                    assertEvent(ev, "CHECKOUT_STARTED")
                    return ev.response.flowType === "CourtesyHold"
                },
                isRestoreCompleted: (ctx) => ctx.restoreCompleted,
                isUserIdSet: (ctx) => ctx.userId !== undefined,
            },
            actions: {
                setUserId: assign<Context, Events>((_, ev) => {
                    assertEvent(ev, "SET_USER_ID")
                    return { userId: ev.userId }
                }),
                setBookingStarted: actions.pure<Context, Events>(() =>
                    assignEventToSend((ctx) => bookingStarted(ctx, windowLocation)),
                ),
                setRestoreCompleted: assign<Context, Events>({ restoreCompleted: true }),
                resetEventToSend: assign<Context, Events>({ eventToSend: undefined }),
                resetUserId: assign<Context, Events>({ userId: undefined }),
                setGuests: actions.pure<Context, Events>((_, ev) => {
                    assertEvent(ev, "GUESTS_CHANGED")
                    return [
                        assign<Context, Events>({ guests: ev.guests }),
                        ...assignEventToSend((ctx) =>
                            buildBookingGuestsChanged({
                                sessionId: ctx.sessionId,
                                cruiseCode: ctx.cruiseCode,
                                returnUrl: returnUrl(ctx, windowLocation),
                                adults: ctx.guests.adults as Int.Int,
                                kids: ctx.guests.kids as Int.Int,
                            }),
                        ),
                    ]
                }),
                setFareCards: assign<Context, Events>({
                    fareCards: (ctx, ev) => {
                        assertEvent(ev, "FARE_CARDS_LOADED")
                        return ev.fareCards
                    },
                }),
                setSelectedFare: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "FARE_SELECTED")
                    return [
                        assign<Context, Events>({ selectedFare: ev.selectedFare }),
                        ...assignBookingFareSelectedEvent(ctx, ev.selectedFare, windowLocation),
                    ]
                }),
                updateFareCards: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "FARE_CARD_CHANGED")
                    return [
                        assign<Context, Events>({
                            fareCards: updateFareCard(ev.fareCard)(ctx.fareCards),
                        }),
                        ...(ev.fareCard.key !== ctx.selectedFare?.key
                            ? []
                            : assignBookingFareSelectedEvent(ctx, ev.fareCard, windowLocation)),
                    ]
                }),
                setSelectedSuiteCategory: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "SUITE_CATEGORY_SELECTED")
                    return [
                        assign<Context, Events>({
                            selectedSuiteCategory: ev.suiteCategory,
                            selectedSuiteCategorySource: "standard",
                        }),
                        ...assignBookingSuiteCategorySelectedEvent(ctx, ev.suiteCategory, windowLocation),
                    ]
                }),
                setSelectedSuiteNumber: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "SUITE_NUMBER_SELECTED")

                    return [
                        assign<Context, Events>({ selectedSuiteNumber: ev.suiteNumber }),
                        ...assignBookingSuiteNumberSelectedEvent(ctx, ev.suiteNumber, windowLocation),
                    ]
                }),
                setSuiteCategoryVisited: actions.pure<Context, Events>((ctx) => {
                    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
                    if (!selectedFare) return []

                    return assignEventToSend((ctx) =>
                        buildBookingSuiteCategoryVisited({
                            sessionId: ctx.sessionId,
                            cruiseCode: ctx.cruiseCode,
                            returnUrl: returnUrl(ctx, windowLocation),
                            adults: ctx.guests.adults as Int.Int,
                            kids: ctx.guests.kids as Int.Int,
                            fare: selectedFare.fare,
                            flights: flightsByFare(selectedFare),
                        }),
                    )
                }),
                setGuestInfoVisited: actions.pure<Context, Events>((ctx) => {
                    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
                    const suiteCategory = ctx.selectedSuiteCategory
                    const suiteNumber = ctx.selectedSuiteNumber
                    if (!selectedFare || !suiteCategory || !suiteNumber) return []

                    return assignEventToSend((ctx) =>
                        buildBookingGuestInfoVisited({
                            sessionId: ctx.sessionId,
                            cruiseCode: ctx.cruiseCode,
                            returnUrl: returnUrl(ctx, windowLocation),
                            adults: ctx.guests.adults as Int.Int,
                            kids: ctx.guests.kids as Int.Int,
                            fare: selectedFare.fare,
                            flights: flightsByFare(selectedFare),
                            suiteCategory,
                            suiteNumber,
                        }),
                    )
                }),
                setGuestInfoSubmitted: actions.pure<Context, Events>((ctx) => {
                    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
                    const suiteCategory = ctx.selectedSuiteCategory
                    const suiteNumber = ctx.selectedSuiteNumber
                    if (!selectedFare || !suiteCategory || !suiteNumber) return []

                    return assignEventToSend((ctx) =>
                        buildBookingGuestInfoSubmitted({
                            sessionId: ctx.sessionId,
                            cruiseCode: ctx.cruiseCode,
                            returnUrl: returnUrl(ctx, windowLocation),
                            adults: ctx.guests.adults as Int.Int,
                            kids: ctx.guests.kids as Int.Int,
                            fare: selectedFare.fare,
                            flights: flightsByFare(selectedFare),
                            suiteCategory,
                            suiteNumber,
                        }),
                    )
                }),
                setCheckoutResponse: assign<Context, Events>({
                    checkoutStatus: (ctx, ev) => {
                        assertEvent(ev, "CHECKOUT_STARTED")
                        return { status: "started", response: ev.response }
                    },
                }),
                setCourtesyHoldCreated: actions.pure<Context, Events>((ctx, ev) => {
                    assertEvent(ev, "CHECKOUT_STARTED")

                    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
                    const suiteCategory = ctx.selectedSuiteCategory
                    const suiteNumber = ctx.selectedSuiteNumber
                    if (ev.response.flowType !== "CourtesyHold" || !selectedFare || !suiteCategory || !suiteNumber)
                        return []

                    return assignEventToSend((ctx) =>
                        buildBookingCourtesyHoldCreated({
                            sessionId: ctx.sessionId,
                            cruiseCode: ctx.cruiseCode,
                            returnUrl: courtesyHoldReturnUrl(ctx, windowLocation),
                            adults: ctx.guests.adults as Int.Int,
                            kids: ctx.guests.kids as Int.Int,
                            fare: selectedFare.fare,
                            flights: flightsByFare(selectedFare),
                            suiteCategory: suiteCategory,
                            suiteNumber: suiteNumber,
                            bookingNumber: ev.response.bookingNumber,
                            expirationDate: DateTime.DateTime.make(ev.response.expiration),
                        }),
                    )
                }),
                setSessionId: assign<Context, Events>({
                    sessionId: (ctx, ev) => {
                        assertEvent(ev, "SESSION_ID_CHANGED")
                        return ev.sessionId
                    },
                }),
                setBookingCompleted: assign<Context, Events>({ eventToSend: buildBookingCompleted() }),
                setBookingDeleted: assign<Context, Events>({ eventToSend: buildBookingDeleted() }),
                sendEvent: (ctx) => (ctx.eventToSend ? sendUserEvent(ctx.eventToSend) : Promise.resolve()),
            },
        },
    )
}

const bookingStarted = (ctx: UserEventsContext, windowLocation: WindowLocation) => {
    const url = windowLocation()
    const returnUrl = new URL(url.pathname, url.origin).href
    return buildBookingStarted({ sessionId: ctx.sessionId, cruiseCode: ctx.cruiseCode, returnUrl })
}

const assignBookingFareSelectedEvent = (ctx: UserEventsContext, fare: FareCardRef, windowLocation: WindowLocation) => {
    const selectedFare = fareCardByKey(ctx, fare.key)
    if (!selectedFare) return []

    return assignEventToSend((ctx) =>
        buildBookingFareSelected({
            sessionId: ctx.sessionId,
            cruiseCode: ctx.cruiseCode,
            returnUrl: returnUrl(ctx, windowLocation),
            adults: ctx.guests.adults as Int.Int,
            kids: ctx.guests.kids as Int.Int,
            fare: selectedFare.fare,
            flights: flightsByFare(selectedFare),
        }),
    )
}

const assignBookingSuiteCategorySelectedEvent = (
    ctx: UserEventsContext,
    suiteCategory: string,
    windowLocation: WindowLocation,
) => {
    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
    if (!selectedFare) return []

    return assignEventToSend((ctx) =>
        buildBookingSuiteCategorySelected({
            sessionId: ctx.sessionId,
            cruiseCode: ctx.cruiseCode,
            returnUrl: returnUrl(ctx, windowLocation),
            adults: ctx.guests.adults as Int.Int,
            kids: ctx.guests.kids as Int.Int,
            fare: selectedFare.fare,
            flights: flightsByFare(selectedFare),
            suiteCategory,
        }),
    )
}

const assignBookingSuiteNumberSelectedEvent = (
    ctx: UserEventsContext,
    suiteNumber: string,
    windowLocation: WindowLocation,
) => {
    const selectedFare = fareCardByKey(ctx, ctx.selectedFare?.key)
    const suiteCategory = ctx.selectedSuiteCategory
    if (!selectedFare || !suiteCategory) return []

    return assignEventToSend((ctx) =>
        buildBookingSuiteNumberSelected({
            sessionId: ctx.sessionId,
            cruiseCode: ctx.cruiseCode,
            returnUrl: returnUrl(ctx, windowLocation),
            adults: ctx.guests.adults as Int.Int,
            kids: ctx.guests.kids as Int.Int,
            fare: selectedFare.fare,
            flights: flightsByFare(selectedFare),
            suiteCategory,
            suiteNumber,
        }),
    )
}

const assignEventToSend = (build: (ctx: UserEventsContext) => PostUserEventsRequest) => [
    assign<UserEventsContext, UserEventsEvents>((ctx) => ({
        eventToSend: build(ctx),
    })),
]

const returnUrl = (ctx: UserEventsContext, windowLocation: WindowLocation) => {
    const url = windowLocation()
    const queryString = presentParamsFromContext(ctx)
    const returnUrl = new URL(url.pathname, url.origin)
    setQueryParams(returnUrl, queryString)
    return returnUrl.href
}

const courtesyHoldReturnUrl = (ctx: UserEventsContext, windowLocation: WindowLocation) => {
    const url = windowLocation()
    const checkoutUrl = redirectCheckoutUrl(ctx.cruiseCode, ctx.sessionId)
    const returnUrl = new URL(checkoutUrl, url.origin)
    return returnUrl.href
}

const flightsByFare = (selectedFare: FareCard): BookingFareSelected["flights"] =>
    AirParams.match({
        notAvailable: () => "None" as const,
        cruiseOnly: () => "None" as const,
        flight: (x) => x.airClass,
    })(selectedFare.air)

export const defaultSendUserEvent =
    (dotComApiBaseUrl: string): SendUserEvent =>
    async (event) => {
        await fetch(new URL("userEvents", dotComApiBaseUrl), {
            method: "POST",
            body: JSON.stringify(event),
            credentials: "include",
        })
    }

const defaultWindowLocation: WindowLocation = () => new URL(window.location.href)

export const useUserEvents = (state: ECommerceState): [UserEventsState, UserEventsSend] => {
    const machine = state.children.userEvents as UserEventsActorRef
    return useActor(machine) as unknown as [UserEventsState, UserEventsSend]
}
